import { Button } from '@cloudflare/kumo';
import { useInfiniteQuery } from '@tanstack/react-query';
import { createRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { idSchema } from '../../shared/content-primitives.ts';
import { api, unwrap } from '../api.ts';
import { ErrorNotice, Loading, proseClasses } from '../common.tsx';
import { Route as parent } from './site.tsx';

export const Route = createRoute({
  getParentRoute: () => parent,
  path: 'site-assistant/inbox',
  validateSearch: z.object({ conversation: idSchema.optional() }).parse,
  component: Inbox,
});

function Inbox() {
  const { siteId } = Route.useParams();
  const selected = Route.useSearch().conversation;
  const conversations = useInfiniteQuery({
    queryKey: ['site-assistant-conversations', siteId],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET('/api/sites/{siteId}/site-assistant/conversations', {
          params: { path: { siteId }, query: pageParam ? { cursor: pageParam } : {} },
        }),
      ),
    getNextPageParam: (page) => page.nextCursor,
    refetchInterval: 15_000,
  });
  const items = conversations.data?.pages.flatMap((page) => page.conversations) ?? [];
  const conversationId = selected ?? items[0]?.id;
  const detail = useInfiniteQuery({
    queryKey: ['site-assistant-conversation', siteId, conversationId],
    initialPageParam: undefined as number | undefined,
    enabled: Boolean(conversationId),
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET('/api/sites/{siteId}/site-assistant/conversations/{conversationId}', {
          params: {
            path: { siteId, conversationId: conversationId! },
            query: pageParam === undefined ? {} : { before: pageParam },
          },
        }),
      ),
    getNextPageParam: (page) => page.before ?? undefined,
    refetchInterval: 15_000,
  });
  const conversation = detail.data?.pages[0]?.conversation;
  const messages = detail.data?.pages.toReversed().flatMap((page) => page.messages) ?? [];
  return (
    <section className="p-4 min-[701px]:p-6 grid gap-5">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-[20px] font-semibold">Site Assistant inbox</h2>
        <Button
          onClick={() => {
            void conversations.refetch();
            if (conversationId) void detail.refetch();
          }}
        >
          Refresh
        </Button>
      </div>
      <ErrorNotice error={conversations.error ?? detail.error} />
      {conversations.isPending ? (
        <Loading />
      ) : !items.length && !selected ? (
        <p className="text-kumo-subtle">No conversations yet.</p>
      ) : (
        <div className="grid gap-5 min-[761px]:grid-cols-[minmax(180px,260px)_minmax(0,1fr)]">
          <div className="grid content-start gap-2" aria-label="Site Assistant conversations">
            {items.map((item) => (
              <Link
                key={item.id}
                to="/sites/$siteId/site-assistant/inbox"
                params={{ siteId }}
                search={{ conversation: item.id }}
                aria-current={item.id === conversationId ? 'true' : undefined}
                className={`grid gap-1 rounded-lg border border-kumo-line p-3 ${item.id === conversationId ? 'bg-kumo-tint' : ''}`}
              >
                <strong className="text-sm break-words">
                  {item.fields.name || item.fields.email || 'Visitor'}
                </strong>
                <time className="text-xs text-kumo-subtle" dateTime={item.lastMessageAt}>
                  {new Date(item.lastMessageAt).toLocaleString()}
                </time>
                <span className="text-xs text-kumo-subtle">
                  {item.formEntryId
                    ? 'Lead captured'
                    : item.submitFormAfter
                      ? 'Awaiting submission'
                      : 'Conversation'}
                </span>
              </Link>
            ))}
            {conversations.hasNextPage && (
              <Button
                disabled={conversations.isFetchingNextPage}
                onClick={() => void conversations.fetchNextPage()}
              >
                Older conversations
              </Button>
            )}
          </div>
          <article
            aria-label="Site Assistant conversation"
            className="min-w-0 grid content-start gap-4 rounded-lg border border-kumo-line p-4"
          >
            {detail.isPending ? (
              <Loading />
            ) : (
              conversation && (
                <>
                  <h3 className="font-semibold">
                    {conversation.formEntryId
                      ? 'Lead captured'
                      : conversation.submitFormAfter
                        ? 'Awaiting submission'
                        : 'Conversation'}
                  </h3>
                  <p className="text-sm text-kumo-subtle">{detail.data?.pages[0]?.form.name}</p>
                  {conversation.initialPageUrl && (
                    <p className="text-sm text-kumo-subtle break-words">
                      Initial page: {conversation.initialPageUrl}
                    </p>
                  )}
                  {!!Object.keys(conversation.fields).length && (
                    <dl className="grid gap-2 text-sm">
                      {Object.entries(conversation.fields).map(([name, value]) => (
                        <div key={name}>
                          <dt className="font-semibold">
                            {detail.data?.pages[0]?.form.fields.find((field) => field.name === name)?.label ??
                              name}
                          </dt>
                          <dd className="whitespace-pre-wrap break-words">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  {conversation.formEntryId && (
                    <Link
                      className="text-sm text-kumo-link underline"
                      to="/sites/$siteId/form-entries"
                      params={{ siteId }}
                    >
                      View Form entries
                    </Link>
                  )}
                  {detail.hasNextPage && (
                    <Button disabled={detail.isFetchingNextPage} onClick={() => void detail.fetchNextPage()}>
                      Older messages
                    </Button>
                  )}
                  <ol className="grid gap-3" aria-label="Conversation transcript">
                    {messages.map((message) => (
                      <li
                        key={message.id}
                        className={`rounded-lg p-3 ${message.role === 'User' ? 'bg-kumo-tint' : 'border border-kumo-line'}`}
                      >
                        <div className="flex flex-wrap gap-2 items-baseline text-xs text-kumo-subtle mb-2">
                          <strong>{message.role === 'User' ? 'Visitor' : 'Assistant'}</strong>
                          <time dateTime={message.createdAt}>
                            {new Date(message.createdAt).toLocaleString()}
                          </time>
                        </div>
                        <div
                          className={`${proseClasses} break-words overflow-x-auto`}
                          dangerouslySetInnerHTML={{ __html: message.html }}
                        />
                      </li>
                    ))}
                  </ol>
                </>
              )
            )}
          </article>
        </div>
      )}
    </section>
  );
}
